// wake-lock.js

/**
 * Keeps the screen on while a timer is counting.
 *
 * A phone left on a bench between rounds hits its auto-lock in thirty seconds
 * on iOS defaults, which is shorter than most of the work segments in this
 * programme -- so the one screen the app builds to be read at arm's length
 * mid-effort is dark exactly when it is being read.
 *
 * One lock is shared by every timer rather than one per instance: the browser
 * only cares whether something wants the screen, and pairing acquire/release
 * per timer would drop the lock every time one timer superseded another.
 *
 * Every failure path here is silent on purpose. The platform declines this for
 * reasons the user already knows about -- low power mode, a backgrounded tab,
 * a browser that never shipped the API -- and none of them are worth
 * interrupting a workout to report. The screen simply dims the way it did
 * before.
 */
export default class WakeLock {
  /** @type {WakeLockSentinel|null} */
  static _sentinel = null;
  /** @type {Promise<boolean>|null} */
  static _pending = null;
  /** Whether anything currently wants the screen held. @type {boolean} */
  static _wanted = false;

  /** Whether this browser exposes the Screen Wake Lock API. @returns {boolean} */
  static get supported() {
    return typeof navigator !== 'undefined' && typeof navigator.wakeLock?.request === 'function';
  }

  /** Whether a lock is held right now. @returns {boolean} */
  static get held() {
    return Boolean(this._sentinel);
  }

  /**
   * Asks the browser to hold the screen awake. Safe to call repeatedly; a
   * second call while one is in flight joins the first rather than racing it.
   * @returns {Promise<boolean>} Whether a lock is held once this settles.
   */
  static async acquire() {
    this._wanted = true;
    if (!this.supported) return false;
    if (this._sentinel) return true;
    if (this._pending) return this._pending;

    this._pending = (async () => {
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        // A release that landed while the request was in flight has to win, or
        // the lock outlives the last running timer with nothing left to
        // release it.
        if (!this._wanted) {
          await Promise.resolve(sentinel.release?.()).catch(() => {});
          return false;
        }
        this._sentinel = sentinel;
        // The platform drops the lock on its own when the tab is hidden. This
        // keeps our view of it honest so a later acquire asks again instead of
        // trusting a sentinel that is no longer doing anything.
        sentinel.addEventListener?.('release', () => {
          if (this._sentinel === sentinel) this._sentinel = null;
        });
        return true;
      } catch {
        return false;
      } finally {
        this._pending = null;
      }
    })();

    return this._pending;
  }

  /**
   * Lets the screen go back to its normal timeout.
   * @returns {Promise<void>}
   */
  static async release() {
    this._wanted = false;
    if (this._pending) await this._pending;
    const sentinel = this._sentinel;
    this._sentinel = null;
    if (!sentinel) return;
    try {
      await sentinel.release?.();
    } catch {
      // Already gone, which is the state we were asking for.
    }
  }
}
